import React from "react";
import { Github, Linkedin, Twitter } from "lucide-react";
import { Link } from "@/components/common/Link";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function CompanyMenuConsumer() {
  const {
    menuItems,
    loading,
    error
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-2">
        <div className="h-4 w-24 bg-muted/20 animate-pulse rounded" />
        <div className="h-4 w-20 bg-muted/20 animate-pulse rounded" />
        <div className="h-4 w-28 bg-muted/20 animate-pulse rounded" />
      </div>;
  }
  const items = error || !menuItems || menuItems.length === 0 ? [{
    id: "about",
    label: "About",
    href: "/AboutPage"
  }, {
    id: "contact",
    label: "Contact Us",
    href: "/ContactUsPage"
  }, {
    id: "privacy",
    label: "Privacy Policy",
    href: "#"
  }, {
    id: "terms",
    label: "Terms of Service",
    href: "#"
  }] : menuItems;
  return <NavigationMenu viewport={false} className="items-start justify-start max-w-full">
      <NavigationMenuList className="flex-col items-start space-x-0 space-y-2">
        {items.map(item => <NavigationMenuItem key={item.id} className="w-full">
            <Link to={item.href || "#"} className="w-full block">
              <NavigationMenuLink className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground hover:bg-transparent focus:bg-transparent transition-colors w-full justify-start font-normal">
                {item.label}
              </NavigationMenuLink>
            </Link>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
export default function Footer() {
  const currentYear = new Date().getFullYear();
  const categories = [{
    title: "Latest Insights",
    href: "/LatestInsightsPage"
  }, {
    title: "Guides & Tutorials",
    href: "/GuidesTutorialsPage"
  }, {
    title: "Performance Analysis",
    href: "#"
  }, {
    title: "Case Studies",
    href: "#"
  }];
  const resources = [{
    title: "Core Web Vitals",
    href: "#"
  }, {
    title: "Speed Tools",
    href: "#"
  }, {
    title: "Documentation",
    href: "#"
  }, {
    title: "API Reference",
    href: "#"
  }];
  return <section id="footer" className="dark bg-background text-foreground py-16 border-t border-border/40">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-16">
          {}
          <div className="lg:col-span-1 space-y-6">
            <Link to="/HomePage" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1555312/img/web_performance_news_logo-light.png" alt="Web Performance News" className="h-7 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              Web Performance News is a dedicated online publication delivering the newest trends, tools, techniques, and insights in web performance to developers and digital leaders.
            </p>
          </div>

          {}
          <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-8">
            {}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold tracking-wide text-foreground uppercase opacity-90">Categories</h4>
              <NavigationMenu viewport={false} className="items-start justify-start max-w-full">
                <NavigationMenuList className="flex-col items-start space-x-0 space-y-2">
                  {categories.map((item, i) => <NavigationMenuItem key={i} className="w-full">
                      <Link to={item.href} className="w-full block">
                        <NavigationMenuLink className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground hover:bg-transparent focus:bg-transparent transition-colors w-full justify-start font-normal">
                          {item.title}
                        </NavigationMenuLink>
                      </Link>
                    </NavigationMenuItem>)}
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            {}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold tracking-wide text-foreground uppercase opacity-90">Resources</h4>
              <NavigationMenu viewport={false} className="items-start justify-start max-w-full">
                <NavigationMenuList className="flex-col items-start space-x-0 space-y-2">
                  {resources.map((item, i) => <NavigationMenuItem key={i} className="w-full">
                      <Link to={item.href} className="w-full block">
                        <NavigationMenuLink className="h-auto p-0 text-sm text-muted-foreground hover:text-foreground hover:bg-transparent focus:bg-transparent transition-colors w-full justify-start font-normal">
                          {item.title}
                        </NavigationMenuLink>
                      </Link>
                    </NavigationMenuItem>)}
                </NavigationMenuList>
              </NavigationMenu>
            </div>

            {}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold tracking-wide text-foreground uppercase opacity-90">Company</h4>
              <MenuProvider menu_id="44" className="w-full">
                <CompanyMenuConsumer />
              </MenuProvider>
            </div>
          </div>
        </div>

        <Separator className="bg-border/40 mb-8" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-xs text-muted-foreground">
            &copy; {currentYear} Web Performance News. All rights reserved.
          </p>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-transparent" asChild>
              <Link to="https://github.com" newTab aria-label="GitHub">
                <Github className="size-4" />
              </Link>
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-transparent" asChild>
              <Link to="https://twitter.com" newTab aria-label="X (Twitter)">
                <Twitter className="size-4" />
              </Link>
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-transparent" asChild>
              <Link to="https://linkedin.com" newTab aria-label="LinkedIn">
                <Linkedin className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}